import { ReplyPublicRelayDto, ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import styled from '@emotion/styled';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { LikedByProfile } from 'app-components/reply/LikedByProfile';
import { ReplyMoreMenu } from 'app-components/reply/ReplyMoreMenu';
import { ReplyEditAction } from 'constants/reply';
import { useAuthState } from 'contexts/AuthProvider';
import { DotDividerIcon } from 'icons/DotDividerIcon';
import { FavoriteEmptyIcon, FavoriteFillIcon } from 'icons/FavoriteIcon';
import { ReplyIcon } from 'icons/ReplyIcon';
import { useRouter } from 'next/navigation';
import React, { useCallback, useMemo } from 'react';
import { useReplyLike } from 'state-hooks/reply/useReplyLike';
import { IconButton } from 'ui-components/common/IconButton';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showAlertModal } from 'utils/modal';

interface ReplyReactionProps {
  reply: ReplyPublicRelayDto;
  isLiked: boolean;
  actions?: ReplyEditAction[];
  authorProfile?: ProfileLitePublicRelayDto;
  onDispatchAction: (action: ReplyEditAction, reply: ReplyPublicRelayDto) => void;
  showMenuButton: boolean;
  onClickReReply?: (reply: ReplyRelayDto) => void;
  showReReplyWrite?: boolean;
  showReReplyCount?: boolean;
  showLike?: boolean;
}

export const ReplyReaction = ({
  reply,
  actions,
  authorProfile,
  showMenuButton,
  isLiked,
  onDispatchAction,
  onClickReReply,
  showReReplyWrite = false,
  showReReplyCount = false,
  showLike = false,
}: ReplyReactionProps) => {
  const router = useRouter();
  const { isLoggedIn, profile: myProfile, user } = useAuthState();
  const requestSign = useRequestSign();
  const requestCertificate = useRequestCertificate();
  const isAuthor = !!(authorProfile && myProfile && authorProfile.id === myProfile.id);
  const { liked, likeCount, toggleLike } = useReplyLike({
    replyId: reply.id,
    isLiked,
    likeCount: reply.counts.likeCount,
  });
  const authorLiked = isAuthor ? liked : reply.isAuthorLiked;
  const isShowReReply = (showReReplyCount && reply.counts.reReplyCount > 0) || showReReplyWrite;

  const reReplyText = useMemo(() => {
    let defaultText = '';
    if (showReReplyWrite) {
      defaultText = '답글 쓰기';
    }
    if (showReReplyCount) {
      defaultText = reply.counts.reReplyCount > 0 ? `${reply.counts.reReplyCount}` : defaultText;
    }
    return defaultText;
  }, [showReReplyWrite, showReReplyCount, reply.counts.reReplyCount]);

  const handleClickLike = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isLoggedIn) {
        requestSign();
        return;
      }

      toggleLike();
    },
    [isLoggedIn, requestSign, toggleLike],
  );

  const handleClickReReply = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      e.preventDefault();

      if (reply.counts.reReplyCount === 0) {
        if (!isLoggedIn) {
          requestSign();
          return;
        }

        if (!user?.ageVerified) {
          showAlertModal(
            { title: '본인인증을 해주세요.', description: '해당 기능을 사용하려면 본인인증이 필요해요.' },
            {
              onConfirm: requestCertificate,
              confirmText: '본인인증하러 가기',
              cancelText: '닫기',
            },
          );
          return;
        }
      }

      if (onClickReReply) {
        // 핸들러가 있는 경우 링크이동하지 않음
        onClickReReply(reply);
        return;
      }

      router.push(
        `/contents/${reply.contentId}/episodes/${reply.episodeId}/replies/${reply.id}?isInputFocus=${
          reply.counts.reReplyCount === 0
        }`,
      );
    },
    [reply, onClickReReply, router, isLoggedIn, user?.ageVerified, requestSign, requestCertificate],
  );

  return (
    <StyledBottom>
      {showLike && (
        <StyledIconButton onClick={handleClickLike}>
          <StyledIconWrapper>
            {liked ? <FavoriteFillIcon color={Color.Primary500} /> : <FavoriteEmptyIcon color={Color.Gray500} />}
          </StyledIconWrapper>
          <StyledFavoriteText>{likeCount > 0 ? likeCount : '좋아요'}</StyledFavoriteText>
          {authorLiked && authorProfile && (
            <StyledAuthorReactionArea>
              <DotDividerIcon color={Color.Gray300} size={3} />
              <LikedByProfile profile={authorProfile} size={16} />
            </StyledAuthorReactionArea>
          )}
        </StyledIconButton>
      )}
      {isShowReReply && (
        <StyledIconButton onClick={handleClickReReply}>
          <StyledIconWrapper>
            <ReplyIcon color={Color.Gray500} className="size-[16px]" />
          </StyledIconWrapper>
          <StyledFavoriteText>{reReplyText}</StyledFavoriteText>
          {reply.isAuthorReplied && authorProfile && (
            <StyledAuthorReactionArea>
              <DotDividerIcon color={Color.Gray300} size={3} />
              <ProfileImage file={authorProfile.profileImage} defaultImageKey={authorProfile.nickname} size={16} />
            </StyledAuthorReactionArea>
          )}
        </StyledIconButton>
      )}
      {showMenuButton && actions && actions.length > 0 && (
        <ReplyMoreMenu
          actions={actions}
          reply={reply}
          onDispatchAction={onDispatchAction}
          hasReReply={reply.counts.reReplyCount > 0}
        />
      )}
    </StyledBottom>
  );
};

const StyledIconButton = styled(IconButton)``;

const StyledBottom = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`;

const StyledIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  margin-right: 4px;
`;

const StyledAuthorReactionArea = styled.div`
  display: flex;
  align-items: center;

  & > * {
    margin-left: 4px;
  }
`;

const StyledFavoriteText = styled.p`
  color: ${Color.Gray500};
  ${Typography.XXXSmall};
`;
